import {observer} from "mobx-react-lite";
import React, {useEffect, useState} from "react";
import EventBooking from "../../../stores/domainStore/pages/models/eventBooking";
import EventBookingsTable from "../eventBookingsTable/eventBookingsTable";
import {EventPaidType} from "../../../shared/enums/eventPaidType";
import {useStores} from "../../../stores/useStores";
import {TextObservable} from "../../../stores/observables/textObservable";
import {Dictionary} from "lodash";
import NoResultsIcon from "../../atoms/noResultsIcon/noResultsIcon";
import {useTranslation} from "react-i18next";

export interface IEventBookingsSearchTableProps {
  searchTerm: TextObservable;
}

const EventBookingsSearchTable: React.FC<IEventBookingsSearchTableProps> = ({
  searchTerm
}) => {
  const {t} = useTranslation("eventTemplate");
  const {
    domainStore: {eventsDomainStore},
    uiStore: {eventsUiStore}
  } = useStores();
  const eventDetails = eventsDomainStore.editableEvent;

  const [liveData, setLiveData] = useState<EventBooking[]>([]);

  useEffect(() => {
    const eventBookings = eventsUiStore.allBookings;

    const loweredSearchTerm = searchTerm.text.toLowerCase();

    const isMatch = (value: string | null | undefined): boolean =>
      (value ?? "").toLowerCase().includes(loweredSearchTerm);

    let searchResults = eventBookings.filter(
      (x) =>
        isMatch(x.residentData.name) ||
        isMatch(x.residentData.unitCode) ||
        isMatch(x.residentData.phoneNumber) ||
        isMatch(x.notes.staffNote) ||
        isMatch(x.notes.residentNote)
    );
    // We need to add the plus 1 bookings. This is a little tricky so here's a truth table of the permutations
    // Scenario 1
    // ** Search Term = Smith
    // ** Main Booking  = John Smith
    // ** Plus1 Booking = Mary Potter
    // ** Result = Add Mary Potter because Mary would not have been in the original filter
    // Scenario 2
    // ** Search Term = Smith
    // ** Main Booking  = John Potter
    // ** Plus1 Booking = Mary Smith
    // ** Result = Add John Potter because John would not have been in the original filter
    // Scenario 3
    // ** Search Term = Smith
    // ** Main Booking  = John Smith
    // ** Plus1 Booking = Mary Smith
    // ** Result = Do nothing, because Mary and John would have been in the original filter
    searchResults.forEach((searchResult) => {
      const plus1BookingPairs = eventsUiStore.getBookingGroup(
        searchResult.creatorId,
        eventBookings
      );
      plus1BookingPairs.forEach((plus1Booking) => {
        if (!searchResults.some((b) => b.id === plus1Booking.id)) {
          searchResults.push(plus1Booking);
        }
      });
    });

    const bookingsByCreatorId: Dictionary<EventBooking[]> =
      eventsUiStore.groupByCreatorId(searchResults);

    const sortGroupResult = Object.values(bookingsByCreatorId).sort(
      (a: EventBooking[], b: EventBooking[]) => {
        return (a[0].residentData.name ?? "") < (b[0].residentData.name ?? "")
          ? -1
          : 1;
      }
    );
    searchResults = sortGroupResult.flat();
    setLiveData(searchResults);

    eventsUiStore.selectedBookingIds.forEach((selectedBookingId) => {
      if (
        !searchResults.some(
          (searchResult) => searchResult.id === selectedBookingId
        )
      ) {
        eventsUiStore.selectedBookingIds.delete(selectedBookingId);
      }
    });
  }, [
    searchTerm.text,
    eventsUiStore,
    eventsUiStore.confirmedEventBookings,
    eventsUiStore.waitlistEventBookings,
    eventsUiStore.cancelledEventBookings
  ]);

  return (
    <>
      <EventBookingsTable
        eventBookings={liveData}
        isSortingEnabled={false}
        showIndex={false}
        isPaidEvent={
          eventDetails?.priceDetails?.paidType.text === EventPaidType.fixed
        }
      />
      {searchTerm.text.length > 0 && liveData.length === 0 && (
        <div className="no-search-result">
          <NoResultsIcon
            titleText={t("eventTemplate:messages.noSearchResults")}
            bodyText={searchTerm.text}
          />
        </div>
      )}
    </>
  );
};

export default observer(EventBookingsSearchTable);
